import type { Landmark, HandLandmark } from "./posture";
import type { CustomHabit, TrainingSample } from "./habitTypes";

/**
 * Result of custom habit detection
 */
export interface CustomHabitDetectionResult {
  /** The habit that was detected, or null if no habit matches */
  habit: CustomHabit | null;
  /** Confidence score (0-1) for the detected habit */
  confidence: number;
  /** Similarity score (0-1) to the best matching training sample */
  similarity: number;
}

/**
 * Calculates normalized distance between two landmarks.
 * Normalizes by a reference length to be size-invariant.
 */
function landmarkDistance(a: Landmark, b: Landmark, refLength: number): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy) / refLength;
}

/**
 * Calculates normalized distance between two hand landmarks.
 * Normalizes by a reference length to be size-invariant.
 */
function handLandmarkDistance(a: HandLandmark, b: HandLandmark, refLength: number): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy) / refLength;
}

/**
 * Computes similarity between current pose/hand data and a training sample.
 * Returns a score from 0 (completely different) to 1 (identical).
 * 
 * Uses normalized coordinates (already in 0-1 range from MediaPipe) for comparison.
 */
function computeSimilarity(
  currentPose: Landmark[],
  currentHands: HandLandmark[][],
  sample: TrainingSample
): number {
  let totalDistance = 0;
  let totalWeight = 0;
  
  // Compare pose landmarks (body keypoints)
  // Focus on upper body landmarks that are most relevant for habits
  const relevantPoseLandmarks = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, // Face and head
    11, 12, 13, 14, 15, 16, // Shoulders, elbows, wrists
  ];
  
  for (const idx of relevantPoseLandmarks) {
    const currentLandmark = currentPose[idx];
    const sampleLandmark = sample.poseLandmarks[idx];
    
    if (!currentLandmark || !sampleLandmark) continue;
    
    const currentVis = currentLandmark.visibility ?? 0;
    const sampleVis = sampleLandmark.visibility ?? 0;
    
    // Only compare if both landmarks are visible
    if (currentVis < 0.5 || sampleVis < 0.5) continue;
    
    // Weight by the minimum visibility
    const weight = Math.min(currentVis, sampleVis);
    
    // Normalize coordinates to [0, 1] range relative to video dimensions
    const currentNorm = {
      x: currentLandmark.x,
      y: currentLandmark.y,
      z: currentLandmark.z,
      visibility: currentLandmark.visibility,
    };
    
    const sampleNorm = {
      x: sampleLandmark.x,
      y: sampleLandmark.y,
      z: sampleLandmark.z,
      visibility: sampleLandmark.visibility,
    };
    
    const distance = landmarkDistance(currentNorm, sampleNorm, 1.0); // Already normalized
    totalDistance += distance * weight;
    totalWeight += weight;
  }
  
  // Compare hand landmarks
  // Match hands by position (closest hand to closest hand)
  const maxHands = Math.min(currentHands.length, sample.handLandmarks.length);
  
  if (maxHands > 0) {
    const handWeight = 2.0;

    // For each hand in the sample, find the closest current hand.
    // If no current hand is detected, treat it as a maximal difference penalty.
    for (let i = 0; i < sample.handLandmarks.length; i++) {
      const sampleHand = sample.handLandmarks[i];
      if (!sampleHand || sampleHand.length === 0) continue;

      let bestHandDistance = Infinity;

      for (let j = 0; j < currentHands.length; j++) {
        const currentHand = currentHands[j];
        if (!currentHand || currentHand.length === 0) continue;

        let handDistance = 0;
        let handPoints = 0;

        for (let k = 0; k < Math.min(currentHand.length, sampleHand.length); k++) {
          const currentPoint = currentHand[k];
          const samplePoint = sampleHand[k];

          if (!currentPoint || !samplePoint) continue;

          const currentNorm = {
            x: currentPoint.x,
            y: currentPoint.y,
            z: currentPoint.z,
          };

          const sampleNorm = {
            x: samplePoint.x,
            y: samplePoint.y,
            z: samplePoint.z,
          };

          const distance = handLandmarkDistance(currentNorm, sampleNorm, 1.0);
          handDistance += distance;
          handPoints++;
        }

        if (handPoints > 0) {
          handDistance /= handPoints;
          bestHandDistance = Math.min(bestHandDistance, handDistance);
        }
      }

      if (isFinite(bestHandDistance)) {
        totalDistance += bestHandDistance * handWeight;
        totalWeight += handWeight;
      } else {
        // No matching hand detected in the current frame; penalize similarity.
        totalDistance += handWeight;
        totalWeight += handWeight;
      }
    }
  } else if (sample.handLandmarks.length > 0) {
    // Current frame has no hands, but the sample expects them.
    const handWeight = 2.0 * sample.handLandmarks.length;
    totalDistance += handWeight;
    totalWeight += handWeight;
  }
  
  if (totalWeight === 0) {
    return 0; // No comparable data
  }
  
  // Average distance
  const avgDistance = totalDistance / totalWeight;
  
  // Convert distance to similarity (closer to 0 is better, so we invert)
  // Use an exponential decay to convert distance to similarity
  // A distance of 0.1 (10% of reference length) gives ~0.6 similarity
  // A distance of 0.05 gives ~0.78 similarity
  const similarity = Math.exp(-avgDistance * 5);
  
  return Math.max(0, Math.min(1, similarity));
}

/**
 * Detects custom habits in the current frame by comparing against training samples.
 * Returns the best matching habit above the confidence threshold.
 */
export function detectCustomHabits(
  poseLandmarks: Landmark[],
  handLandmarks: HandLandmark[][],
  enabledHabits: CustomHabit[],
  videoWidth: number,
  videoHeight: number,
  similarityThreshold: number = 0.65 // Minimum similarity to consider a match
): CustomHabitDetectionResult {
  let bestHabit: CustomHabit | null = null;
  let bestSimilarity = 0;
  let bestConfidence = 0;
  let bestScore = -Infinity;
  
  for (const habit of enabledHabits) {
    const habitSamples = habit.habitSamples ?? [];
    const neutralSamples = habit.neutralSamples ?? [];

    if (!habit.enabled || habitSamples.length === 0) {
      continue;
    }
    
    let maxHabitSimilarity = 0;
    let maxNeutralSimilarity = 0;
    
    for (const sample of habitSamples) {
      const similarity = computeSimilarity(
        poseLandmarks,
        handLandmarks,
        sample
      );
      
      maxHabitSimilarity = Math.max(maxHabitSimilarity, similarity);
    }

    if (neutralSamples.length > 0) {
      for (const sample of neutralSamples) {
        const similarity = computeSimilarity(
          poseLandmarks,
          handLandmarks,
          sample
        );
        maxNeutralSimilarity = Math.max(maxNeutralSimilarity, similarity);
      }
    }
    
    const neutralPenalty = maxNeutralSimilarity * 0.7; // neutral similarity reduces confidence
    const score = maxHabitSimilarity - neutralPenalty;

    // Require minimum similarity and separation from neutral
    const separationThreshold = neutralSamples.length > 0 ? 0.1 : 0; // need at least 0.1 difference if neutral exists
    const passesThreshold =
      maxHabitSimilarity >= similarityThreshold &&
      score >= separationThreshold;

    if (passesThreshold && score > bestScore) {
      bestScore = score;
      bestSimilarity = maxHabitSimilarity;
      bestHabit = habit;
      // Confidence increases with similarity and number of training samples
      // More samples = more confidence in the match
      const sampleBonus = Math.min(habitSamples.length / 10, 0.2);
      const separationBonus = Math.max(0, score);
      bestConfidence = Math.min(1, maxHabitSimilarity + sampleBonus + separationBonus / 2);
    }
  }
  
  return {
    habit: bestHabit,
    confidence: bestConfidence,
    similarity: bestSimilarity,
  };
}
